<template>
  <a
    v-if="href"
    class="accessibility-outline"
    :class="classes"
    :style="styles"
    :href="href"
    :title="title"
    :target="target"
    :aria-label="computedAriaLabel || undefined"
    :aria-describedby="ariaDescribedBy || undefined"
    :aria-pressed="ariaPressed !== null ? ariaPressed : undefined"
    :aria-expanded="ariaExpanded !== null ? ariaExpanded : undefined"
    :aria-disabled="disabled"
    :role="computedRole"
    :tabindex="disabled ? -1 : (tabindex !== null ? tabindex : undefined)"
    @click="handleClick"
    @keydown.enter.self.stop="handleKeydown"
    @keydown.space.self.stop="handleKeydown"
  >
    <slot />
  </a>
  <button
    v-else
    class="accessibility-outline"
    :class="classes"
    :style="styles"
    :title="title"
    :type="role"
    :disabled="disabled"
    :aria-label="computedAriaLabel || undefined"
    :aria-describedby="ariaDescribedBy || undefined"
    :aria-pressed="ariaPressed !== null ? ariaPressed : undefined"
    :aria-expanded="ariaExpanded !== null ? ariaExpanded : undefined"
    :aria-disabled="disabled"
    :role="computedRole"
    :tabindex="disabled ? -1 : (tabindex !== null ? tabindex : undefined)"
    @click="handleClick"
    @keydown.enter.self.stop.prevent="handleKeydown"
    @keydown.space.self.stop.prevent="handleKeydown"
  >
    <slot />
  </button>
</template>

<script setup lang="ts">
import { computed, useSlots, PropType } from 'vue';

type ButtonRole = 'button' | 'submit' | 'reset' | undefined;
type ButtonType = 'outlined' | 'filled' | 'link' | 'tile' | 'fab' | 'icon';
type ButtonColor = 'primary' | 'secondary' | 'tertiary' | 'text' | 'inactive' | 'warning';
type AriaPressed = boolean | 'true' | 'false' | 'mixed' | null;
type AriaExpanded = boolean | 'true' | 'false' | null;

const props = defineProps({
  type: {
    type: String as PropType<ButtonType>,
    default: 'filled',
  },
  color: {
    type: String as PropType<ButtonColor>,
    default: 'primary',
  },
  small: {
    type: Boolean,
    default: false,
  },
  medium: {
    type: Boolean,
    default: false,
  },
  active: {
    type: Boolean,
    default: false,
  },
  href: {
    type: String,
    default: '',
  },
  title: {
    type: String,
    default: '',
  },
  target: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  ariaLabel: {
    type: String,
    default: '',
  },
  ariaDescribedBy: {
    type: String,
    default: '',
  },
  ariaPressed: {
    type: [Boolean, String] as PropType<AriaPressed>,
    default: null,
  },
  ariaExpanded: {
    type: [Boolean, String] as PropType<AriaExpanded>,
    default: null,
  },
  role: {
    type: String as PropType<ButtonRole>,
    default: 'button',
  },
  tabindex: {
    type: [Number, String] as PropType<number | string>,
    default: null,
  },
});

const slots = useSlots();

const slotContent = computed(() => slots.default?.());

const emit = defineEmits<{
  (e: 'click', event: Event): void;
  (e: 'keydown', event: KeyboardEvent): void;
  (e: 'update:ariaPressed', value: AriaPressed): void;
}>();

const classes = computed(() => ({
  'base-button': true,
  [`base-button--${props.type}`]: true,
  'base-button--small': props.small,
  'base-button--medium': props.medium,
  'base-button--disabled': props.disabled,
  'base-button--active': props.active,
}));

const computedRole = computed(() => {
  // If role is explicitly set, use it
  if (props.role) {
    return props.role;
  }

  // If ariaPressed is set, it's likely a toggle button
  if (props.ariaPressed !== null) {
    return 'button';
  }

  // For link type when rendered as button, ensure proper role
  if (!props.href && props.type === 'link') {
    return 'button';
  }

  // Default: let the browser determine the role
  return undefined;
});

const computedAriaLabel = computed(() => {
  if (props.ariaLabel) {
    return props.ariaLabel;
  }
  return props.title || slotContent.value;
});

const styles = computed(() => ({
  '--base-button-color': `var(--button-color-${props.color})`,
  '--base-button-color_hover': `var(--button-color-${props.color}_hover)`,
  ...(props.type === 'link' ? { '--button-border-radius': '4px' } : {}),
}));

const handleClick = (event: Event) => {
  if (props.disabled) {
    event.preventDefault();
    return;
  }
  emit('click', event);
};

const handleKeydown = (event: KeyboardEvent) => {
  event.preventDefault();
  if (props.disabled) {
    return;
  }

  // If this is a submit button inside a form, trigger the form submission.
  // This ensures submit listeners (like confirm dialogs) are executed.
  const target = event.currentTarget as HTMLElement | null;
  if (!props.href && target) {
    const buttonEl = target as HTMLButtonElement;
    const isSubmitButton = (buttonEl.tagName === 'BUTTON' && buttonEl.type === 'submit') || props.role === 'submit';

    if (isSubmitButton) {
      const formEl = buttonEl.form as HTMLFormElement | null;
      if (formEl) {
        // Prefer requestSubmit so the submit event is dispatched and constraints are checked
        // @ts-ignore - requestSubmit may not exist in older lib.dom definitions
        if (typeof formEl.requestSubmit === 'function') {
          // @ts-ignore
          formEl.requestSubmit(buttonEl);
        } else {
          formEl.submit();
        }
        return;
      }
    }
  }

  if (props.href) {
    window.location.href = props.href;
  }

  emit('click', event);

  // For toggle buttons, update aria-pressed if it's a boolean
  if (props.ariaPressed !== null && typeof props.ariaPressed === 'boolean') {
    emit('update:ariaPressed', !props.ariaPressed);
  }

  emit('keydown', event);
};
</script>

<style lang="scss" scoped>
.base-button {
  all: unset;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: var(--button-icon-padding);
  padding: var(--button-padding);
  font-size: var(--button-font-size);
  font-weight: var(--button-font-weight);
  border-radius: var(--button-border-radius);
  border-width: var(--button-border-width);
  transition: var(--button-transition);
  white-space: nowrap;
  line-height: 1;
  cursor: pointer;

  &:hover {
    text-decoration: none;
  }

  &--small {
    padding: var(--button-padding-small);
    font-size: var(--button-font-size-small);
    font-weight: var(--button-font-weight-small);
  }

  &--medium {
    padding: var(--button-padding-medium);
    font-size: var(--button-font-size-medium);
    font-weight: var(--button-font-weight-medium);
  }

  &--outlined {
    border-style: solid;
    border-color: var(--base-button-color);
    background-color: transparent;
    color: var(--base-button-color);

    &:hover,
    &:focus {
      border-color: var(--base-button-color);
      background-color: var(--base-button-color);
      color: #fff;
    }
  }

  &--filled {
    border-style: solid;
    border-color: var(--base-button-color);
    background-color: var(--base-button-color);
    color: #fff;

    &:hover,
    &:focus {
      border-color: var(--base-button-color_hover);
      background-color: var(--base-button-color_hover);
    }
  }

  &--link {
    border-color: transparent;
    background-color: transparent;
    color: var(--base-button-color);

    &:hover,
    &:focus {
      color: var(--base-button-color_hover);
    }
  }

  &--tile {
    border-radius: 5px;
    border-style: solid;
    border-color: var(--base-button-color);
    background-color: var(--base-button-color);
    color: #fff;

    &:hover,
    &:focus {
      border-color: var(--base-button-color_hover);
      background-color: var(--base-button-color_hover);
    }
  }

  &--fab {
    border-color: transparent;
    background-color: transparent;
    color: var(--base-button-color);

    &:hover,
    &:focus {
      box-shadow: 0 0 5px 1px currentColor !important;
    }
  }

  &--icon {
    border-color: transparent;
    background-color: transparent;
    color: var(--base-button-color);
  }

  &--disabled {
    opacity: var(--button-opacity_disabled);
    pointer-events: none;
  }
}

.accessibility-outline {
  &:focus {
    outline-offset: 2px !important;
    outline: 2px solid var(--base-button-color, #377ef8) !important;
  }
}
</style>
